import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtNum, fmtDateTime, tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { toast } from "sonner";
import { Target, TrendingUp, Layers, Send, CheckCircle2, Sparkles, AlertTriangle } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const METRICS = ["sales", "traffic", "transactions", "conversion"];
const fmt = (v: number | null, metric: string) => v == null ? "—" : (metric === "sales" ? fmtMoney(v) : metric === "conversion" ? `${v}%` : fmtNum(Math.round(v)));
const confColor: Record<string, string> = { high: "good", low: "warn", none: "critical" };
const statusColor: Record<string, string> = { published: "good", approved: "info", submitted: "info", draft: "warn", superseded: "info", archived: "critical", rejected: "critical" };

export const GoalsPanel: React.FC = () => {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const [stores, setStores] = useState<any[]>([]);
  const [storeId, setStoreId] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/org/stores").then((r) => { const l = r.data.stores || []; setStores(l); if (l[0]) setStoreId(l[0].id); })
      .catch(() => setStores([])).finally(() => setLoaded(true));
  }, []);
  if (!loaded) return <LoadingBlock />;
  if (stores.length === 0) return <EmptyState icon={<Target size={26} />} title="No stores in scope" description="No stores available for goals." />;
  return (
    (<div data-testid="goals-panel">
      <div className="mb-4 flex items-center gap-2">
        <Label className="text-xs text-muted-foreground">{t("Store")}</Label>
        <select className="rounded-md border bg-background px-3 py-1.5 text-sm" value={storeId} onChange={(e) => setStoreId(e.target.value)} data-testid="goals-store-select">
          {stores.map((s) => <option key={s.id} value={s.id}>#{s.store_number} · {s.name}</option>)}
        </select>
      </div>
      <Tabs defaultValue="summary">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="summary" data-testid="goals-tab-summary">{t("Actual vs Forecast vs Goal")}</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="goals-tab-forecast">{t("Forecast")}</TabsTrigger>
          <TabsTrigger value="goals" data-testid="goals-tab-goals">{t("Goals")}</TabsTrigger>
          <TabsTrigger value="rollup" data-testid="goals-tab-rollup">{t("Rollup")}</TabsTrigger>
          <TabsTrigger value="accuracy" data-testid="goals-tab-accuracy">{t("Accuracy")}</TabsTrigger>
        </TabsList>
        {storeId && <>
          <TabsContent value="summary"><SummaryView storeId={storeId} /></TabsContent>
          <TabsContent value="forecast"><ForecastView storeId={storeId} /></TabsContent>
          <TabsContent value="goals"><GoalsList storeId={storeId} canPropose={hasPerm("goal.propose")} canApprove={hasPerm("goal.approve")} /></TabsContent>
          <TabsContent value="rollup"><RollupView /></TabsContent>
          <TabsContent value="accuracy"><AccuracyView storeId={storeId} /></TabsContent>
        </>}
      </Tabs>
    </div>)
  );
};

const SummaryView: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [d, setD] = useState<any>(null);
  useEffect(() => { setD(null); api.get(`/goals/shift-summary?store_id=${storeId}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [storeId]);
  if (!d) return <LoadingBlock />;
  if (d.error) return <EmptyState icon={<AlertTriangle size={24} />} title="Unavailable" description="Could not load the summary." />;
  return (
    (<div className="space-y-3" data-testid="goals-summary">
      <div className="text-xs text-muted-foreground">{t("As of")}{" "}{fmtDateTime(d.as_of)}{" "}{t(
          "· Actual = recorded · Forecast = system estimate · Goal = approved target"
        )}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        {d.metrics.map((m: any) => (
          <Card key={m.metric} className="p-4" data-testid={`goal-summary-${m.metric}`}>
            <div className="mb-2 flex items-center justify-between">
              <div className="font-display text-sm font-semibold capitalize">{m.metric}</div>
              <StatusPill status={confColor[m.confidence] || "info"}>{stat(m.confidence)}{" "}{t("confidence")}</StatusPill>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-[11px] uppercase text-muted-foreground">{t("Actual")}</div><div className="text-lg font-semibold" data-testid={`goal-actual-${m.metric}`}>{fmt(m.actual, m.metric)}</div></div>
              <div><div className="text-[11px] uppercase text-muted-foreground">{t("Forecast")}</div><div className="text-lg font-semibold text-brand">{fmt(m.forecast, m.metric)}</div></div>
              <div><div className="text-[11px] uppercase text-muted-foreground">{t("Goal")}</div><div className="text-lg font-semibold">{fmt(m.goal, m.metric)}</div></div>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t("Forecasted finish:")}{" "}<b>{fmt(m.forecasted_finish, m.metric)}</b> <span className="opacity-60">({m.forecasted_finish_basis})</span></span>
              {m.variance_to_goal && m.variance_to_goal.pct != null &&
                <span className={m.variance_to_goal.abs >= 0 ? t("text-status-good") : t("text-status-critical")} data-testid={`goal-variance-${m.metric}`}>
                  {m.variance_to_goal.abs >= 0 ? "+" : ""}{m.variance_to_goal.pct}{t("% vs goal")}</span>}
            </div>
            {m.forecast_status !== "ok" && <div className="mt-1 text-[11px] text-status-warn">{t("Forecast:")}{" "}{m.forecast_status.replace(/_/g, " ")}</div>}
          </Card>
        ))}
      </div>
    </div>)
  );
};

const ForecastView: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [metric, setMetric] = useState("sales");
  const [level, setLevel] = useState("daily");
  const [d, setD] = useState<any>(null);
  const load = useCallback(() => { setD(null); api.get(`/goals/forecast?store_id=${storeId}&metric=${metric}&level=${level}`).then((r) => setD(r.data)).catch(() => setD({ status: "error" })); }, [storeId, metric, level]);
  useEffect(() => { load(); }, [load]);
  return (
    (<div className="space-y-3" data-testid="goals-forecast">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={metric} onValueChange={setMetric}><SelectTrigger className="w-40" data-testid="forecast-metric"><SelectValue /></SelectTrigger><SelectContent>{METRICS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent></Select>
        <Select value={level} onValueChange={setLevel}><SelectTrigger className="w-36" data-testid="forecast-level"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">{t("Daily")}</SelectItem><SelectItem value="weekly">{t("Weekly")}</SelectItem><SelectItem value="monthly">{t("Monthly")}</SelectItem></SelectContent></Select>
      </div>
      {!d ? <LoadingBlock /> : (
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2 font-display text-sm font-semibold"><TrendingUp size={16} className="text-brand" />{t("Forecast ·")}{" "}{d.model}v{d.model_version}</div>
            <StatusPill status={d.status === "ok" ? "good" : d.status === "low_confidence" ? "warn" : "critical"}>{(d.status || "").replace(/_/g, " ")}</StatusPill>
          </div>
          {["missing_data", "not_configured", "error"].includes(d.status) ? (
            <EmptyState icon={<AlertTriangle size={22} />} title={(d.status || "").replace(/_/g, " ")} description={d.explanation || "No forecast available."} />
          ) : (
            <>
              <div className="text-xs text-muted-foreground">{d.explanation}</div>
              {d.input_window && <div className="mt-1 text-[11px] text-muted-foreground">{t("Input window:")}{" "}{d.input_window.first}→ {d.input_window.last}· {d.input_window.history_points}{" "}{t("points · source")}{" "}{d.provenance?.source}</div>}
              <div className="mt-3 max-h-64 space-y-1 overflow-auto">
                {(level === "daily" ? d.daily : d.periods).map((row: any, i: number) => (
                  <div key={i} className="flex items-center justify-between border-b border-border/30 py-1 text-sm" data-testid={`forecast-row-${i}`}>
                    <span>{level === "daily" ? row.date : `${row.period_start} → ${row.period_end}`}{row.closed && <span className="ml-2 text-[11px] text-status-critical">{t("closed")}</span>}</span>
                    <span className="font-medium">{fmt(row.value, metric)}</span>
                  </div>
                ))}
              </div>
              {d.limitations?.length > 0 && <ul className="mt-2 list-disc pl-5 text-[11px] text-status-warn">{d.limitations.map((l: string, i: number) => <li key={i}>{l}</li>)}</ul>}
            </>
          )}
        </Card>
      )}
    </div>)
  );
};

const GoalsList: React.FC<{ storeId: string; canPropose: boolean; canApprove: boolean }> = ({ storeId, canPropose, canApprove }) => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ metric: "sales", period_type: "monthly", period_start: "", period_end: "", amount: "" });
  const load = useCallback(() => { setLoading(true); api.get(`/goals?scope_type=store&scope_id=${storeId}`).then((r) => setRows(r.data.goals || [])).finally(() => setLoading(false)); }, [storeId]);
  useEffect(() => { load(); }, [load]);
  const act = async (id: string, action: string, body?: any) => {
    try { await api.post(`/goals/${id}/${action}`, body || {}); toast.success(action); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || tl("Failed to {action}", { action })); }
  };
  const create = async () => {
    if (!form.period_start || !form.period_end || !form.amount) { toast.error("Fill period + amount"); return; }
    try {
      await api.post("/goals", { scope_type: "store", scope_id: storeId, metric: form.metric, period_type: form.period_type, period_start: form.period_start, period_end: form.period_end, amount: Number(form.amount), source: canApprove ? "top_down" : "proposal" });
      toast.success("Goal draft created"); setCreating(false); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (loading) return <LoadingBlock />;
  return (
    (<div className="space-y-3" data-testid="goals-list">
      {canPropose && (creating ? (
        <Card className="p-4">
          <div className="mb-2 text-sm font-semibold">{t("New goal")}</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <div><Label className="text-xs">{t("Metric")}</Label><Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}><SelectTrigger className="mt-1" data-testid="goal-form-metric"><SelectValue /></SelectTrigger><SelectContent>{METRICS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">{t("Period")}</Label><Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="weekly">{t("Weekly")}</SelectItem><SelectItem value="monthly">{t("Monthly")}</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">{t("Amount")}</Label><Input type="number" className="mt-1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="goal-form-amount" /></div>
            <div><Label className="text-xs">{t("Start")}</Label><Input type="date" className="mt-1" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="goal-form-start" /></div>
            <div><Label className="text-xs">{t("End")}</Label><Input type="date" className="mt-1" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="goal-form-end" /></div>
          </div>
          <div className="mt-3 flex gap-2"><Button size="sm" onClick={create} data-testid="goal-form-save">{t("Create draft")}</Button><Button size="sm" variant="ghost" onClick={() => setCreating(false)}>{t("Cancel")}</Button></div>
        </Card>
      ) : <Button size="sm" onClick={() => setCreating(true)} data-testid="goal-create-btn"><Sparkles size={13} className="mr-1" />{t("New goal")}</Button>)}
      {rows.length === 0 ? <EmptyState icon={<Target size={24} />} title="No goals" description="Create a goal draft to begin." /> : rows.map((g) => (
        <Card key={g.id} className="p-3" data-testid={`goal-${g.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium capitalize">{g.metric} · {g.period_type} · {fmt(g.amount, g.metric)}</div>
              <div className="text-xs text-muted-foreground">{g.period_start}→ {g.period_end}{" "}{t("· v")}{g.version_no}· {g.source}</div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <StatusPill status={statusColor[g.status] || "info"}>{stat(g.status)}</StatusPill>
              {canPropose && g.status === "draft" && <Button size="sm" variant="outline" onClick={() => act(g.id, "submit")} data-testid={`goal-submit-${g.id}`}><Send size={12} className="mr-1" />{t("Submit")}</Button>}
              {canApprove && ["submitted", "draft"].includes(g.status) && <Button size="sm" variant="outline" onClick={() => act(g.id, "approve")} data-testid={`goal-approve-${g.id}`}>{t("Approve")}</Button>}
              {canApprove && g.status === "approved" && <Button size="sm" onClick={() => act(g.id, "publish")} data-testid={`goal-publish-${g.id}`}><CheckCircle2 size={12} className="mr-1" />{t("Publish")}</Button>}
            </div>
          </div>
        </Card>
      ))}
    </div>)
  );
};

const RollupView: React.FC = () => {
  const {
    t
  } = useI18n();

  const [d, setD] = useState<any>(null);
  const [pt, setPt] = useState("monthly");
  useEffect(() => { setD(null); api.get(`/goals/rollup?metric=sales&period_type=${pt}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [pt]);
  return (
    (<div className="space-y-3" data-testid="goals-rollup">
      <Select value={pt} onValueChange={setPt}><SelectTrigger className="w-36"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="weekly">{t("Weekly")}</SelectItem><SelectItem value="monthly">{t("Monthly")}</SelectItem></SelectContent></Select>
      {!d ? <LoadingBlock /> : d.error ? <EmptyState icon={<Layers size={24} />} title="Unavailable" /> : (
        <Card className="p-4">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold"><Layers size={16} className="text-brand" />{t("Sales goal rollup (")}{d.store_goal_count}{" "}{t("store goals)")}</div>
          <div className="text-2xl font-semibold" data-testid="rollup-tenant-total">{fmtMoney(d.sum_of_store_goals.tenant_total || 0)}</div>
          <div className="text-xs text-muted-foreground">{t("Sum of leaf store goals (no double counting)")}</div>
          <div className="mt-3 space-y-1 text-sm">
            {Object.entries(d.sum_of_store_goals.by_district || {}).map(([k, v]: any) => <div key={k} className="flex justify-between border-b border-border/30 py-1"><span className="text-muted-foreground">{t("District …")}{k.slice(-6)}</span><span className="font-medium">{fmtMoney(v)}</span></div>)}
          </div>
          {d.directly_set_parent_goals?.length > 0 && <div className="mt-3 text-[11px] text-muted-foreground">{d.directly_set_parent_goals.length}{" "}{t(
              "directly-set parent goal(s) shown for comparison — not added to the store sum."
            )}</div>}
        </Card>
      )}
    </div>)
  );
};

const AccuracyView: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [metric, setMetric] = useState("sales");
  const [d, setD] = useState<any>(null);
  useEffect(() => { setD(null); api.get(`/goals/accuracy?store_id=${storeId}&metric=${metric}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [storeId, metric]);
  return (
    (<div className="space-y-3" data-testid="goals-accuracy">
      <Select value={metric} onValueChange={setMetric}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent>{METRICS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent></Select>
      {!d ? <LoadingBlock /> : d.error ? <EmptyState icon={<TrendingUp size={24} />} title="Unavailable" /> : (
        <Card className="p-4">
          <div className="mb-2 font-display text-sm font-semibold">{t("Forecast accuracy ·")}{" "}{d.pairs}{" "}{t("paired days")}</div>
          {d.pairs === 0 ? <div className="text-sm text-muted-foreground">{t("No forecast-vs-actual pairs yet.")}</div> : (
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-[11px] uppercase text-muted-foreground">MAPE</div><div className="text-xl font-semibold">{d.metrics.mape ?? "—"}%</div></div>
              <div><div className="text-[11px] uppercase text-muted-foreground">MAE</div><div className="text-xl font-semibold">{d.metrics.mae ?? "—"}</div></div>
              <div><div className="text-[11px] uppercase text-muted-foreground">{t("Bias")}</div><div className="text-xl font-semibold">{d.metrics.bias ?? "—"}</div></div>
            </div>
          )}
          <div className="mt-2 text-[11px] text-muted-foreground">{d.note}</div>
        </Card>
      )}
    </div>)
  );
};
